import { useEffect, useState } from 'react';
import { useCreateAccountMutation } from 'services/accounts';
import { AvailableAccountsQuery, AvailableAccountsResponse, CreateGroupAccountPayload } from 'services/accounts/types';
import { useCreateMerchantMutation } from 'services/merchants';
import { MerchantPayoutFlow } from 'services/merchants/types';
import { useLazyGetAvailableAccountsQuery } from 'services/users';

import { AccountType, PwcCustomerType } from 'types/account';
import { CurrencyCode } from 'types/currency';
import { RequestPagination, ServerPagination } from 'types/pagination';

import { getServerSideValidationDetails } from 'utils/error';

import { getDefaultPagination, undecoratePagination } from '../../../utils/pagination';
import { SelectItem } from '../../inputs/select/select';
import { KEYS } from './keys';
import { CreatedAccount } from './types';

const mapAvailableAccountsToSelectItems = (response: unknown, allowedTypes?: AccountType[]): SelectItem[] => {
  if (typeof response !== 'object' || response === null || !('data' in response)) {
    return [];
  }

  const payload = (response as { data?: AvailableAccountsResponse }).data;
  if (!payload?.data) {
    return [];
  }

  const records = allowedTypes?.length ? payload.data.filter((item) => allowedTypes.includes(item.account.type)) : payload.data;

  return records.map((item) => ({
    key: item.account.uuid,
    label: item.account.name,
  }));
};

const payoutFlowFromCustomerType = (customerType: PwcCustomerType): MerchantPayoutFlow =>
  customerType === PwcCustomerType.Business ? MerchantPayoutFlow.SETTLEMENT_WALLET : MerchantPayoutFlow.DIRECT;

export const useCreateAccountModal = () => {
  const [isCreateAccountModalOpen, setCreateAccountModalOpen] = useState<boolean>(false);
  const [accountType, setAccountType] = useState<AccountType>(AccountType.Pwc);
  const [pwcCustomerType, setPwcCustomerType] = useState<PwcCustomerType>(PwcCustomerType.Business);
  const [createdAccount, setCreatedAccount] = useState<CreatedAccount | null>(null);
  const [createAccount, { isLoading: isSubmittingAccount, error: createAccountError, reset: resetCreateAccount }] =
    useCreateAccountMutation();
  const [createMerchant, { isLoading: isSubmittingMerchant, error: createMerchantError, reset: resetCreateMerchant }] =
    useCreateMerchantMutation();
  const [fetchAvailableAccounts] = useLazyGetAvailableAccountsQuery();
  const handleCreateAccountModalOpen = () => {
    setAccountType(AccountType.Pwc);
    setCreatedAccount({
      username: '',
      firstName: '',
      lastName: '',
      accountName: '',
      shift4MerchantId: '',
      groupAccountId: '',
      parentAccountUuid: '',
      parentAccountName: undefined,
      mainFiatCurrency: CurrencyCode.USD,
    });
    setCreateAccountModalOpen(true);
  };

  useEffect(() => {
    if (isCreateAccountModalOpen) {
      const pagination = undecoratePagination(getDefaultPagination());
      fetchAvailableAccounts({ pagination, filters: { type: AccountType.Pwc_Group } }, true);
      fetchAvailableAccounts({ pagination, filters: { type: AccountType.PSP } }, true);
    }
  }, [isCreateAccountModalOpen, fetchAvailableAccounts]);

  const handleCreateAccountModalClose = () => {
    setCreatedAccount(null);
    setCreateAccountModalOpen(false);
  };

  const getGroupAccounts = async (params: {
    search?: string;
    pagination?: RequestPagination;
    ids?: string | string[];
  }): Promise<{ data: SelectItem[]; pagination?: ServerPagination }> => {
    const query: AvailableAccountsQuery = {
      pagination: params.pagination ?? undecoratePagination(getDefaultPagination()),
      filters: { type: AccountType.Pwc_Group },
    };

    if (params.search) {
      query.search = params.search;
    }

    const response = await fetchAvailableAccounts(query, true);

    if ('data' in response && response.data) {
      let records = mapAvailableAccountsToSelectItems(response, [AccountType.Pwc_Group]);
      if (params.ids) {
        const idList = (Array.isArray(params.ids) ? params.ids : [params.ids]).filter(Boolean);
        records = records.filter((r) => idList.includes(r.key));
      }
      return {
        data: records,
        pagination: response.data.pagination,
      };
    }

    return { data: [] };
  };

  const getParentAccounts = async (params: {
    search?: string;
    pagination?: RequestPagination;
    ids?: string | string[];
  }): Promise<{ data: SelectItem[]; pagination?: ServerPagination }> => {
    const pagination = {
      page: 1,
      itemsPerPage: 100,
    };

    const queryBase: Omit<AvailableAccountsQuery, 'filters'> = {
      pagination,
      ...(params.search ? { search: params.search } : {}),
    };

    const [pspResponse, groupResponse] = await Promise.all([
      fetchAvailableAccounts({ ...queryBase, filters: { type: AccountType.PSP } }, true),
      fetchAvailableAccounts({ ...queryBase, filters: { type: AccountType.Pwc_Group } }, true),
    ]);

    const mergedMap = new Map<string, SelectItem>();
    for (const item of mapAvailableAccountsToSelectItems(pspResponse, [AccountType.PSP])) {
      mergedMap.set(item.key, item);
    }
    for (const item of mapAvailableAccountsToSelectItems(groupResponse, [AccountType.Pwc_Group])) {
      mergedMap.set(item.key, item);
    }

    let data = [...mergedMap.values()].sort((a, b) => (a.label || '').localeCompare(b.label || ''));

    if (params.ids) {
      const idList = (Array.isArray(params.ids) ? params.ids : [params.ids]).filter(Boolean);
      data = data.filter((r) => idList.includes(r.key));
    }

    const count =
      ('data' in pspResponse && pspResponse.data?.pagination?.count ? pspResponse.data.pagination.count : 0) +
      ('data' in groupResponse && groupResponse.data?.pagination?.count ? groupResponse.data.pagination.count : 0);

    return {
      data,
      pagination: {
        page: 1,
        itemsPerPage: 100,
        count,
      },
    };
  };

  const handleCreateAccount = async (formValues: CreatedAccount) => {
    const { shift4MerchantId, groupAccountId, mainFiatCurrency, parentAccountUuid } = formValues;

    if (accountType === AccountType.Pwc_Group) {
      const ownerFullName = [formValues.firstName, formValues.lastName]
        .map((s) => (s ?? '').trim())
        .filter(Boolean)
        .join(' ');

      const groupPayload: CreateGroupAccountPayload = {
        accountType: AccountType.Pwc_Group,
        parentAccountUuid,
        accountName: formValues.accountName,
        ownerEmail: formValues.username,
        ...(ownerFullName ? { ownerFullName } : {}),
      };
      const result = await createAccount(groupPayload);
      return 'data' in result;
    }

    const ownerFullName = [formValues.firstName, formValues.lastName]
      .map((s) => (s ?? '').trim())
      .filter(Boolean)
      .join(' ');

    const merchantPayload = {
      name: formValues.accountName,
      ownerEmail: formValues.username,
      payoutFlow: payoutFlowFromCustomerType(pwcCustomerType),
      ...(ownerFullName ? { ownerFullName } : {}),
      ...(shift4MerchantId ? { shift4MerchantId } : {}),
      ...(groupAccountId ? { groupAccountId } : {}),
      ...(pwcCustomerType === PwcCustomerType.Business && mainFiatCurrency ? { mainFiatCurrency } : {}),
    };

    const result = await createMerchant(merchantPayload);
    return 'data' in result;
  };

  const resetMutations = () => {
    resetCreateAccount();
    resetCreateMerchant();
  };

  const accountErrors = getServerSideValidationDetails(createAccountError, KEYS.FORM_FIELDS.EMAIL);
  const merchantErrors = getServerSideValidationDetails(createMerchantError, KEYS.FORM_FIELDS.EMAIL);
  const createAccountErrorMessage = {
    ...(accountErrors ?? {}),
    ...(merchantErrors ?? {}),
  };

  return {
    handleCreateAccountModalOpen,
    createdAccount,
    modalProps: {
      isSubmitting: isSubmittingAccount || isSubmittingMerchant,
      reset: resetMutations,
      open: isCreateAccountModalOpen,
      onClose: handleCreateAccountModalClose,
      onCreateAccount: handleCreateAccount,
      account: createdAccount!,
      error: createAccountErrorMessage,
      setAccountType,
      accountType,
      getGroupAccounts,
      getParentAccounts,
      setPwcCustomerType,
      pwcCustomerType,
    },
  };
};
